import fabric from '@/lib/fabric';

type Pt = { x: number; y: number };

function dist(a: Pt, b: Pt): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

function sampleBezier(
  p0: Pt,
  p1: Pt,
  p2: Pt,
  p3: Pt | null,
  steps: number
): Pt[] {
  const out: Pt[] = [];
  for (let i = 0; i <= steps; i += 1) {
    const t = i / steps;
    const mt = 1 - t;
    if (p3) {
      // cubic
      out.push({
        x:
          mt * mt * mt * p0.x +
          3 * mt * mt * t * p1.x +
          3 * mt * t * t * p2.x +
          t * t * t * p3.x,
        y:
          mt * mt * mt * p0.y +
          3 * mt * mt * t * p1.y +
          3 * mt * t * t * p2.y +
          t * t * t * p3.y,
      });
    } else {
      // quadratic
      out.push({
        x: mt * mt * p0.x + 2 * mt * t * p1.x + t * t * p2.x,
        y: mt * mt * p0.y + 2 * mt * t * p1.y + t * t * p2.y,
      });
    }
  }
  return out;
}

function transformPts(obj: fabric.Object, locals: Pt[]): Pt[] {
  const matrix = obj.calcTransformMatrix();
  const offset = (obj as fabric.Path).pathOffset;
  return locals.map((p) => {
    const lx = offset ? p.x - offset.x : p.x;
    const ly = offset ? p.y - offset.y : p.y;
    const tp = fabric.util.transformPoint(new fabric.Point(lx, ly), matrix);
    return { x: tp.x, y: tp.y };
  });
}

function flattenPathCommands(path: fabric.Path): Pt[] {
  // Fabric typings call this Point[], but runtime values are command tuples.
  const cmds = (path.path || []) as unknown as Array<Array<string | number>>;
  const pts: Pt[] = [];
  let cx = 0;
  let cy = 0;
  let startX = 0;
  let startY = 0;

  for (const cmd of cmds) {
    const op = String(cmd[0]);
    if (op === 'M') {
      cx = Number(cmd[1]);
      cy = Number(cmd[2]);
      startX = cx;
      startY = cy;
      pts.push({ x: cx, y: cy });
    } else if (op === 'L') {
      cx = Number(cmd[1]);
      cy = Number(cmd[2]);
      pts.push({ x: cx, y: cy });
    } else if (op === 'Q') {
      const samples = sampleBezier(
        { x: cx, y: cy },
        { x: Number(cmd[1]), y: Number(cmd[2]) },
        { x: Number(cmd[3]), y: Number(cmd[4]) },
        null,
        12
      );
      pts.push(...samples.slice(1));
      cx = Number(cmd[3]);
      cy = Number(cmd[4]);
    } else if (op === 'C') {
      const samples = sampleBezier(
        { x: cx, y: cy },
        { x: Number(cmd[1]), y: Number(cmd[2]) },
        { x: Number(cmd[3]), y: Number(cmd[4]) },
        { x: Number(cmd[5]), y: Number(cmd[6]) },
        16
      );
      pts.push(...samples.slice(1));
      cx = Number(cmd[5]);
      cy = Number(cmd[6]);
    } else if (op === 'Z') {
      pts.push({ x: startX, y: startY });
      cx = startX;
      cy = startY;
    }
  }
  return transformPts(path, pts);
}

function outlineFromObject(obj: fabric.Object): Pt[] | null {
  const type = (obj.type || '').toLowerCase();

  if (type === 'path') {
    const pts = flattenPathCommands(obj as fabric.Path);
    return pts.length >= 2 ? pts : null;
  }

  if (type === 'line') {
    const line = obj as fabric.Line;
    const local = line.calcLinePoints();
    const matrix = line.calcTransformMatrix();
    const p1 = fabric.util.transformPoint(new fabric.Point(local.x1, local.y1), matrix);
    const p2 = fabric.util.transformPoint(new fabric.Point(local.x2, local.y2), matrix);
    return [
      { x: p1.x, y: p1.y },
      { x: p2.x, y: p2.y },
    ];
  }

  if (type === 'polyline' || type === 'polygon') {
    const pts = ((obj as fabric.Polyline).points || []) as Pt[];
    if (pts.length < 2) return null;
    const matrix = obj.calcTransformMatrix();
    const offset = (obj as fabric.Polyline).pathOffset || { x: 0, y: 0 };
    const mapped = pts.map((p) => {
      const tp = fabric.util.transformPoint(
        new fabric.Point(p.x - offset.x, p.y - offset.y),
        matrix
      );
      return { x: tp.x, y: tp.y };
    });
    if (type === 'polygon' && mapped.length) {
      mapped.push({ ...mapped[0] });
    }
    return mapped;
  }

  if (type === 'ellipse' || type === 'circle') {
    const b = obj.getBoundingRect(true);
    const cx = b.left + b.width / 2;
    const cy = b.top + b.height / 2;
    const rx = b.width / 2;
    const ry = b.height / 2;
    const n = 64;
    const out: Pt[] = [];
    for (let i = 0; i <= n; i += 1) {
      const a = (i / n) * Math.PI * 2 - Math.PI / 2;
      out.push({ x: cx + Math.cos(a) * rx, y: cy + Math.sin(a) * ry });
    }
    return out;
  }

  if (type === 'rect' || type === 'triangle') {
    const coords = obj.getCoords(true);
    if (!coords?.length) return null;
    const out = coords.map((p) => ({ x: p.x, y: p.y }));
    out.push({ ...out[0] });
    return out;
  }

  return null;
}

function resampleByArcLength(
  points: Pt[],
  count: number
): Array<{ x: number; y: number; angle: number }> {
  if (points.length < 2 || count < 1) return [];

  const segs: number[] = [0];
  let total = 0;
  for (let i = 1; i < points.length; i += 1) {
    total += dist(points[i - 1], points[i]);
    segs.push(total);
  }
  if (total < 1e-6) return [];

  const result: Array<{ x: number; y: number; angle: number }> = [];
  for (let i = 0; i < count; i += 1) {
    const target = (i / Math.max(1, count - 1)) * total;
    let s = 1;
    while (s < segs.length && segs[s] < target) s += 1;
    const i1 = Math.max(1, s);
    const i0 = i1 - 1;
    const t0 = segs[i0];
    const t1 = segs[i1];
    const u = t1 === t0 ? 0 : (target - t0) / (t1 - t0);
    const a = points[i0];
    const b = points[i1];
    const x = a.x + (b.x - a.x) * u;
    const y = a.y + (b.y - a.y) * u;
    const angle = (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
    result.push({ x, y, angle });
  }
  return result;
}

export function canPlaceTextOnPath(obj: fabric.Object | null | undefined): boolean {
  if (!obj) return false;
  const type = (obj.type || '').toLowerCase();
  return (
    type === 'path' ||
    type === 'line' ||
    type === 'polyline' ||
    type === 'polygon' ||
    type === 'ellipse' ||
    type === 'circle' ||
    type === 'rect' ||
    type === 'triangle'
  );
}

/**
 * Place characters along the outline of a path/line/shape.
 * Returns a group of per-glyph Text objects.
 */
export function placeTextOnPath(
  canvas: fabric.Canvas,
  pathObj: fabric.Object,
  text: string,
  options?: { fontSize?: number; fill?: string; fontFamily?: string }
): { ok: boolean; note: string; group?: fabric.Group } {
  const trimmed = text.trim();
  if (!trimmed) {
    return { ok: false, note: 'Enter some text first' };
  }
  if (!canPlaceTextOnPath(pathObj)) {
    return { ok: false, note: 'Select a path, line, or shape edge' };
  }

  const outline = outlineFromObject(pathObj);
  if (!outline || outline.length < 2) {
    return { ok: false, note: 'Could not sample that path' };
  }

  const chars = [...trimmed];
  const samples = resampleByArcLength(outline, chars.length);
  if (samples.length < chars.length) {
    return { ok: false, note: 'Path is too short for that text' };
  }

  const fontSize = options?.fontSize ?? 28;
  const fill =
    options?.fill ||
    (typeof pathObj.stroke === 'string' && pathObj.stroke) ||
    (typeof pathObj.fill === 'string' && pathObj.fill) ||
    '#152033';
  const fontFamily = options?.fontFamily || 'Georgia, serif';

  const glyphs = chars.map((ch, i) => {
    const s = samples[i];
    return new fabric.Text(ch, {
      left: s.x,
      top: s.y,
      fontSize,
      fill,
      fontFamily,
      originX: 'center',
      originY: 'center',
      angle: s.angle,
      selectable: false,
      evented: false,
    });
  });

  const group = new fabric.Group(glyphs, {
    selectable: true,
    evented: true,
    subTargetCheck: false,
  });
  (group as fabric.Object & { name?: string }).name = 'Text on path';

  canvas.add(group);
  canvas.setActiveObject(group);
  canvas.requestRenderAll();

  return { ok: true, note: 'Text placed on path', group };
}
